import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { isWebsiteRoute, websiteTabsFor } from './WebsiteNav';
import { getRegistrations, getNewInquiryCount } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import './Sidebar.css';

interface SidebarProps {
  onLogout: () => void;
}

function Sidebar({ onLogout }: SidebarProps) {
  const { user, clubName, email, plan, refreshPlan, features } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  // A club with no badge, or one whose file has gone missing, falls back to the
  // initial. Without this a broken crest sits where the club's identity should.
  const [badgeBroken, setBadgeBroken] = useState(false);
  const location = useLocation();
  const displayName = clubName || 'Admin';
  const displayEmail = email || '';
  const isFree = plan === 'free';
  // Players visibility is handled inside the Club Management module
  // (TeamNav gates the Players tab on features.players); the sidebar
  // just links to the module entry point.
  // Email is a Club + Pro feature. Plan-gated only (matches the lambda's
  // ELIGIBLE_PLANS set in lambda/routes/email.ts).
  const emailListEnabled = plan === 'club' || plan === 'pro';
  // Collections (membership/fee collection) — staged per-club flag.
  const collectionsEnabled = features?.collections === true;
  // KiCKS integration — operator-set per club (docs/features/kicks-integration.md).
  const kicksEnabled = features?.kicksIntegration === true;
  // Fixtures & results — dark-launch pilot, per-club flag.
  const fixturesEnabled = features?.fixtures === true;
  const registrationEnabled = features?.registration === true;
  // Enquiries inbox: Club + Pro, the same plan gate as email.
  const enquiriesEnabled = plan === 'club' || plan === 'pro';

  // Unread-style count on Registrations. Only 'verified' rows count — those are
  // the ones a human has to decide on. Pending rows are unconfirmed and the API
  // withholds them anyway, so badging them would send clubs to an empty screen.
  const [pendingRegs, setPendingRegs] = useState(0);
  useEffect(() => {
    if (!registrationEnabled) return;
    let alive = true;
    const refresh = () => getRegistrations()
      .then(rows => { if (alive) setPendingRegs(rows.filter(r => r.status === 'verified').length); })
      .catch(() => {});
    refresh();
    // Re-count when the Registrations page acts, on a slow poll while the tab
    // is visible, and the moment it becomes visible again. Polling a hidden
    // tab cannot stop a badge looking stale — nobody is reading it — and it
    // is what made /preview/status the busiest endpoint on the platform, so
    // this one is guarded the same way from the start.
    const onVisible = () => { if (!document.hidden) refresh(); };
    window.addEventListener('registrations-changed', refresh);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      refresh();
    }, 60000);
    return () => {
      alive = false;
      window.removeEventListener('registrations-changed', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [registrationEnabled]);

  // New enquiries badge, kept fresh the same way as Registrations.
  const [newEnquiries, setNewEnquiries] = useState(0);
  useEffect(() => {
    if (!enquiriesEnabled) return;
    let alive = true;
    const refresh = () => getNewInquiryCount()
      .then(n => { if (alive) setNewEnquiries(n); })
      .catch(() => {});
    refresh();
    const onVisible = () => { if (!document.hidden) refresh(); };
    window.addEventListener('enquiries-changed', refresh);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      refresh();
    }, 60000);
    return () => {
      alive = false;
      window.removeEventListener('enquiries-changed', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [enquiriesEnabled]);
  // Custom Domain (BYOD) — operator-set feature flag only. We DON'T gate
  // on plan any more: Pro gets BYOD included, Club/Free can subscribe as
  // a paid add-on (£0.99/mo on Club, £2.99/mo on Free), so every plan that has
  // the flag on needs to see the entry to either manage their sub or
  // see the upsell CTA. Server still 402s anyone without a valid path.

  // Refresh plan from server on mount (in case user upgraded via Stripe)
  useEffect(() => {
    refreshPlan();
  }, []);

  // Close sidebar when route changes (mobile navigation)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        className="hamburger-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
      >
        <span className={`hamburger-line ${isOpen ? 'open' : ''}`} />
        <span className={`hamburger-line ${isOpen ? 'open' : ''}`} />
        <span className={`hamburger-line ${isOpen ? 'open' : ''}`} />
      </button>

      {/* Mobile overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={() => setIsOpen(false)} />}

      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <h1 className="sidebar-title">{clubName || 'GrassrootsFC'}</h1>
        <p className="sidebar-subtitle">Admin Dashboard</p>
      </div>

      <div className="sidebar-user">
        {user?.badgeUrl && !badgeBroken ? (
          <img
            className="user-avatar"
            src={user.badgeUrl}
            alt=""
            onError={() => setBadgeBroken(true)}
          />
        ) : (
          <div className="user-avatar">
            {displayName.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="user-info">
          <p className="user-name">{displayName}</p>
          <p className="user-email">{displayEmail}</p>
        </div>
      </div>

      <div className="sidebar-plan-badge">
        <span className={`plan-badge ${plan}`}>
          {plan === 'pro' ? 'Pro Plan' : plan === 'club' ? 'Club Plan' : 'Free Plan'}
        </span>
      </div>

      <nav className="sidebar-nav">
        <p className="nav-group-title">Overview</p>
        <NavLink
          to="/dashboard"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <img className="nav-icon" src="/images/nav/dashboard.png" alt="" />
          <span className="nav-text">Dashboard</span>
        </NavLink>

        

        <p className="nav-group-title">People &amp; money</p>
        {registrationEnabled && (
          <NavLink
            to="/registrations"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/images/nav/registrations.png" alt="" />
            <span className="nav-text">Registrations</span>
            {pendingRegs > 0 && (
              <span className="nav-badge" aria-label={`${pendingRegs} waiting for a decision`}>
                {pendingRegs > 99 ? '99+' : pendingRegs}
              </span>
            )}
          </NavLink>
        )}

        
        <NavLink
          to="/teams"
          className={`nav-item ${['/teams', '/players', '/coaches', '/members', '/guardians'].includes(location.pathname) ? 'active' : ''}`}
        >
          <img className="nav-icon" src="/images/nav/club-management.png" alt="" />
          <span className="nav-text">Club Management</span>
        </NavLink>

        
        {collectionsEnabled && (
          <NavLink
            to="/collections"
            className={`nav-item ${location.pathname.startsWith('/collections') ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/images/nav/collections.png" alt="" />
            <span className="nav-text">Collections</span>
          </NavLink>
        )}
        {kicksEnabled && (
          <NavLink
            to="/kicks"
            className={`nav-item ${location.pathname.startsWith('/kicks') ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/images/nav/kicks.png" alt="" />
            <span className="nav-text">KiCKS integration</span>
          </NavLink>
        )}

        

        <p className="nav-group-title">The season</p>
        {fixturesEnabled && (
          <NavLink
            to="/fixtures"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/images/nav/fixtures.png" alt="" />
            <span className="nav-text">Fixtures</span>
          </NavLink>
        )}

        

        <p className="nav-group-title">Your website</p>
        <NavLink
          to="/content"
          className={`nav-item ${isWebsiteRoute(location.pathname) ? 'active' : ''}`}
        >
          <img className="nav-icon" src="/images/nav/website.png" alt="" />
          <span className="nav-text">Website</span>
        </NavLink>

        {/* The nine pages, as small text, while you are in the section. Not
            shown from elsewhere in the admin — expanding permanently would put
            the sidebar back at 21 entries, which is what this replaced. Hidden
            on mobile by CSS, where the tab strip takes over instead. */}
        {isWebsiteRoute(location.pathname) && (
          <div className="nav-sub">
            {websiteTabsFor(plan).map(t => (
              <NavLink
                key={t.to}
                to={t.gated ? '/upgrade' : t.to}
                className={({ isActive }) =>
                  `nav-sub-item ${t.gated ? 'gated' : ''} ${isActive && !t.gated ? 'active' : ''}`
                }
              >
                {t.label}
                {t.gated && <span className="upgrade-badge">Upgrade</span>}
              </NavLink>
            ))}
          </div>
        )}

        <p className="nav-group-title">Reach out</p>
        {emailListEnabled && (
          <NavLink
            to="/email/compose"
            className={({ isActive }) =>
              `nav-item ${isActive || location.pathname.startsWith('/email/') ? 'active' : ''}`
            }
          >
            <img className="nav-icon" src="/images/nav/email.png" alt="" />
            <span className="nav-text">Send Email</span>
          </NavLink>
        )}
        {/* Shown to everyone. A free club sees it with an Upgrade badge rather
            than not at all: it is something they can buy, and hiding it meant
            they never learned it existed. Same treatment News already had.
            The gated link goes to /upgrade because App only registers the
            /enquiries route when the plan allows it, so pointing at it would
            bounce them to the dashboard. */}
        <NavLink
          to={enquiriesEnabled ? '/enquiries' : '/upgrade'}
          className={({ isActive }) =>
            `nav-item ${enquiriesEnabled ? '' : 'gated'} ${isActive && enquiriesEnabled ? 'active' : ''}`
          }
        >
          <img className="nav-icon" src="/images/nav/inquiries.png" alt="" />
          <span className="nav-text">Enquiries</span>
          {enquiriesEnabled
            ? newEnquiries > 0 && (
                <span className="nav-badge" aria-label={`${newEnquiries} new`}>
                  {newEnquiries > 99 ? '99+' : newEnquiries}
                </span>
              )
            : <span className="upgrade-badge">Upgrade</span>}
        </NavLink>

        

        <p className="nav-group-title">Account</p>
        {/* One or the other, never both — a free club is asked to upgrade,
            a paying club gets its billing. */}
        {isFree ? (
          <NavLink
            to="/upgrade"
            className={({ isActive }) => `nav-item nav-item-upgrade ${isActive ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/images/nav/upgrade.png" alt="" />
            <span className="nav-text">Upgrade</span>
          </NavLink>
        ) : (
          <NavLink
            to="/upgrade"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <img className="nav-icon" src="/images/nav/billing.png" alt="" />
            <span className="nav-text">Billing</span>
          </NavLink>
        )}

        
        {/* Shown to every club, linking to the real page rather than /upgrade.
            The domain is an add-on any plan can buy (£2.99/mo on Free, 99p on
            Club, included on Pro) and that page carries its own pricing and
            Subscribe button, so it IS the upgrade path. No badge: there is
            nothing to upgrade TO, they just buy it. */}
        <NavLink
          to="/custom-domain"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <img className="nav-icon" src="/images/nav/custom-domain.png" alt="" />
          <span className="nav-text">Custom Domain</span>
        </NavLink>

        
        <NavLink
          to="/settings"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        >
          <img className="nav-icon" src="/images/nav/settings.png" alt="" />
          <span className="nav-text">Settings</span>
        </NavLink>
      


</nav>

      <div className="sidebar-footer">
        <button onClick={onLogout} className="logout-btn">
          <img className="nav-icon" src="/images/nav/logout.png" alt="" />
          <span className="nav-text">Logout</span>
        </button>
      </div>
    </aside>
    </>
  );
}

export default Sidebar;
